<template>
    <Modal
      v-if="show"
      dimmed
      :additionalContainerStyles="{
        width: '100%',
        overflow: 'hidden',
        'justify-content': 'center',
      }"
      :additionalModalStyles="{
        width: '90%',
        'max-width': '500px',
        border: 'none',
        'overflow-y': 'scroll',
        'max-height': '90%',
      }"
    >
      <div class="form-container card-shape modal-window">
    		<h2 class="form-header-title">Grant Limit Reached</h2>
        <div class="fcolumn">
          <div class="frow maxedModalSection">
            <template v-if="maxedLifetimeGrantLimit">
              <div
                class="frow"
                v-translate="{
                  grantLimit: grantLimitLifetimeDollars
                }"
              >
                Unfortunately, you have already reached the lifetime grant limit of %{grantLimit}.
              </div>
            </template>
            <template v-else>
              <div
                class="frow"
                v-if="organizationDetails.grantLimitDuration == 'Lifetime'"
                v-translate="{
                  grantlimit: grantLimitDollars,
                  grantCountLimit: getScopedOrgProperty('grantCountLimit')
                }"
              >
                Unfortunately, you have already reached the total grant limit of %{grantlimit}, or
                received %{grantCountLimit} grants.
              </div>
              <div
                class="frow"
                v-if="organizationDetails.grantLimitDuration == 'Calendar Year'"
                v-translate="{
                  grantlimit: grantLimitDollars,
                  grantCountLimit: getScopedOrgProperty('grantCountLimit')
                }"
              >
                Unfortunately, you have already reached the total grant limit of %{grantlimit}, or
                received %{grantCountLimit} grants, in this calendar year.
              </div>
              <div
                class="frow"
                v-if="organizationDetails.grantLimitDuration.startsWith('Rolling')"
                v-translate="{
                  grantlimit: grantLimitDollars,
                  grantCountLimit: getScopedOrgProperty('grantCountLimit'),
                  num: organizationDetails.rollingGrantDuration,
                }"
              >
                Unfortunately, you have already reached the total grant limit of %{grantlimit}, or
                received %{grantCountLimit} grants, in a rolling %{num}-year period.
              </div>
            </template>
          </div>
          <div class="frow maxedModalSection" v-if="!maxedLifetimeGrantLimit">
            <div
              class="frow"
              v-if="organizationDetails.grantLimitDuration == 'Calendar Year'"
              v-translate="{
                nextYear: nextYear,
              }"
            >
              You will be able to request additional grant funds on January 1 %{nextYear}.
            </div>
            <div
              class="frow"
              v-if="organizationDetails.grantLimitDuration.startsWith('Rolling')"
              v-translate="{
                date: dateToReApply,
              }"
            >
              You will be able to request additional grant funds on %{date}.
            </div>
          </div>
          <div class="frow maxedModalSection" v-if="organizationDetails.additionalResourcesCopy">
            <div v-html="additionalResources"></div>
          </div>
          <div class="frow maxedModalSection">
            <p v-translate>We are sorry you are experiencing financial hardship. We wish you the best.</p>
          </div>
          <div class="form-footer frow maxedModalSection" style="justify-content: center">
            <router-link
              :to="{ name: 'Home' }"
              tag="button"
              class="form-button"
              style="color: white;"
            >
              <span v-translate>Home</span>
            </router-link>
          </div>
        </div>
      </div>
    </Modal>
</template>

<script>
import { mapState } from 'vuex'
import { formatDateShort, formatNumberAsUSD } from '@/services/utils'
import Modal from '@/components/Modal'

export default {
  name: 'GrantLimitModal',
  components: {
  	Modal,
  },
  props: {
    show: {
    	type: Boolean,
    	default: true,
    },
  },
  data() {
  	return {
  		today: new Date(),
  	}
  },
  methods: {
  	getScopedOrgProperty(propName) {
      // Empty strings and zero are considered null/undefined/not-set
      const orgValue = this.organizationDetails && this.organizationDetails[propName]
      const subOrgValue = this.user.subOrganizationRef && this.user.subOrganizationRef[propName]

      return subOrgValue || orgValue
    },
  },
  computed: {
  	...mapState(['user', 'organizationDetails']),
    grantLimitDollars() {
      const amountLimit = this.getScopedOrgProperty("grantLimit")
      return formatNumberAsUSD(amountLimit, true) || "N/A"
    },
    grantLimitLifetimeDollars() {
      const amountLimit = this.getScopedOrgProperty("grantLimitLifetime")
      if(amountLimit)
        return 
      return formatNumberAsUSD(amountLimit, true) || "N/A"
    },
    dateToReApply() {
      // Push this logic to backend if we need to use this in multiple places
      var initial_date = new Date(this.user.rollingGrantDate).getTime()
      var final_date = new Date(
        initial_date + this.organizationDetails.rollingGrantDuration * 365 * 24 * 60 * 60 * 1000,
      )
      return formatDateShort(final_date.getTime(), this.$language.current)
    },
  	maxedGrantLimit() {
      const amountLimit = this.getScopedOrgProperty('grantLimit')
      const countLimit = this.getScopedOrgProperty('grantCountLimit')

      const hasLimits = amountLimit || countLimit

      if (!hasLimits) { return false }

      // Lifetime grant limit
      if (this.maxedLifetimeGrantLimit) {
        return true
      }

      const currentAmount = this.user.totalPayoutsSinceLastGrantLimitReset
      const currentCount = this.user.totalNumberPayoutsSinceLastGrantLimitReset

      const overAmount = amountLimit ? currentAmount >= amountLimit : false
      const overCount = countLimit ? currentCount >= countLimit : false

      return overAmount || overCount
    },
    maxedLifetimeGrantLimit() {
      const amountLimitLifetime = this.getScopedOrgProperty('grantLimitLifetime')
      const currentAmountLifetime = this.user.totalPayouts
      if(amountLimitLifetime) {
        return currentAmountLifetime >= amountLimitLifetime
      }

      return false
    },
    nextYear() {
      return this.today.getFullYear() + 1
    },
    additionalResources() {
      return this.organizationDetails.additionalResourcesCopy
    },
  },
}
</script>

<style lang="scss" scoped>
@import '@/styles/variables';
@import '@/styles/flex-box';
@import '@/styles/mixins/buttons';
@import '@/styles/mixins/containers';
@import '@/styles/grant-circles';

.maxedModalSection {
  margin: 1rem 0 0 0;
  justify-content: start;
  align-items: center;
}
</style>